import type { CreativeGenerationEntity } from '../../../openapi/models/CreativeGenerationEntity'
import type { CreativeGenerationResponseDto } from '../../../openapi/models/CreativeGenerationResponseDto'

/**
 * Client-side typed view of the SocialKit v2 rubric, decoupled from the loosely
 * typed generated OpenAPI models (the engine's jsonb fields come across as
 * `Record<string, any>`). These mirror `apps/socialkit/src/core/visual/v2/types.ts`.
 */

export type GateResult = 'PASS' | 'FAIL'

export interface RubricGates {
  specificity: GateResult
  truth: GateResult
  visualIntegrity: GateResult
  ownerPride: GateResult
}

export interface RubricDimensions {
  specificity: number
  voice: number
  visualCraft: number
  hook: number
  brandFit: number
  focus: number
  proof: number
  cta: number
  languageHygiene: number
  format: number
  coherence: number
}

export type RubricVerdict =
  'SHIP' | 'SHIP_WITH_ONE_FIX' | 'REVISE' | 'REJECT' | 'NO_SHIP_GATE_FAIL' | string

/** The composer's normalized view of one creative's captured process. */
export interface CreativeProcessSnapshot {
  score: number
  verdict: RubricVerdict
  gates?: RubricGates
  dimensions?: RubricDimensions
  swapTest?: string
  singleFix?: string
  fixStage?: string
  designScore?: number
  designTier?: string
  brief?: Record<string, unknown>
  angle?: Record<string, unknown>
  revisionHistory?: RevisionEntry[]
  metrics?: Record<string, unknown>
  scoredAt?: string
  source?: string
}

export interface RevisionEntry {
  feedback: string
  plan?: Record<string, unknown>
  scoreBefore: number
  scoreAfter: number
  at: string
}

export interface ScoreHistoryPoint {
  score: number
  verdict: RubricVerdict
  source?: string
  createdAt: string
}

/** Display metadata for the eleven rubric dimensions, in render order. */
export const DIMENSION_META: ReadonlyArray<{
  key: keyof RubricDimensions
  label: string
  weight: number
}> = [
  { key: 'specificity', label: 'Specificity', weight: 3 },
  { key: 'voice', label: 'Voice', weight: 3 },
  { key: 'visualCraft', label: 'Visual craft', weight: 3 },
  { key: 'hook', label: 'Hook', weight: 2 },
  { key: 'brandFit', label: 'Brand fit', weight: 2 },
  { key: 'focus', label: 'Focus', weight: 1 },
  { key: 'proof', label: 'Proof', weight: 1 },
  { key: 'cta', label: 'CTA', weight: 1 },
  { key: 'languageHygiene', label: 'Language', weight: 1 },
  { key: 'format', label: 'Format', weight: 1 },
  { key: 'coherence', label: 'Coherence', weight: 1 },
]

/**
 * The carousel set-level rubric is a DIFFERENT scorecard than the static post: 10
 * deck-level dimensions scored 1-10 (not the 11 static dimensions on 1-5). A carousel
 * snapshot carries these under `dimensions`; the panel renders them with this meta + scale.
 */
export const CAROUSEL_DIMENSION_MAX = 10

export const CAROUSEL_DIMENSION_META: ReadonlyArray<{
  key: string
  label: string
  weight?: number
}> = [
  { key: 'hookStrength', label: 'Hook', weight: 2 },
  { key: 'narrativeArc', label: 'Arc', weight: 2 },
  { key: 'slideTwo', label: 'Slide 2' },
  { key: 'oneIdea', label: 'One idea', weight: 2 },
  { key: 'momentum', label: 'Momentum' },
  { key: 'cohesion', label: 'Cohesion', weight: 2 },
  { key: 'roleVariation', label: 'Variation' },
  { key: 'cta', label: 'CTA' },
  { key: 'lengthDiscipline', label: 'Length' },
  { key: 'finish', label: 'Finish' },
]

/**
 * The reel video rubric: 10 clip-level dimensions scored 1-10 (the weighted "great" bar
 * the video judge applies). A scored reel carries these under `dimensions`; the panel
 * renders them with this meta + scale. NOTE: video shares `hookStrength`/`cta`/`finish`
 * with the carousel rubric, so detection MUST check video (motionAuthenticity is unique)
 * BEFORE carousel.
 */
export const VIDEO_DIMENSION_MAX = 10

export const VIDEO_DIMENSION_META: ReadonlyArray<{ key: string; label: string; weight?: number }> =
  [
    { key: 'hookStrength', label: 'Hook', weight: 2 },
    { key: 'wouldPost', label: 'Would post', weight: 2 },
    { key: 'motionAuthenticity', label: 'Authenticity', weight: 2 },
    { key: 'pacing', label: 'Pacing', weight: 2 },
    { key: 'specificity', label: 'Specificity', weight: 2 },
    { key: 'captionLegible', label: 'Captions' },
    { key: 'brandLook', label: 'Brand look' },
    { key: 'arc', label: 'Arc' },
    { key: 'cta', label: 'CTA' },
    { key: 'coherence', label: 'Coherence' },
    { key: 'finish', label: 'Finish' },
  ]

/** Whether a dimensions blob is the reel video rubric (unique: motionAuthenticity). */
export function isVideoDimensions(
  dimensions: Record<string, unknown> | RubricDimensions | null | undefined
): boolean {
  return !!dimensions && 'motionAuthenticity' in dimensions
}

/** Whether a dimensions blob is the carousel set-level rubric (vs the static 11). */
export function isCarouselDimensions(
  dimensions: Record<string, unknown> | RubricDimensions | null | undefined
): boolean {
  return !!dimensions && 'hookStrength' in dimensions
}

export const GATE_META: ReadonlyArray<{ key: keyof RubricGates; label: string }> = [
  { key: 'specificity', label: 'Specific' },
  { key: 'truth', label: 'True to facts' },
  { key: 'visualIntegrity', label: 'Visual integrity' },
  { key: 'ownerPride', label: 'Owner pride' },
]

export type ScoreTier = 'strong' | 'solid' | 'weak'

/** Strong >= 85, Solid >= 70, else weak. Used only for accent color, not gating. */
export function scoreTier(score: number): ScoreTier {
  if (score >= 85) return 'strong'
  if (score >= 70) return 'solid'
  return 'weak'
}

const VERDICT_LABELS: Record<string, string> = {
  SHIP: 'Ship',
  SHIP_WITH_ONE_FIX: 'Ship with one fix',
  REVISE: 'Revise',
  REJECT: 'Reject',
  NO_SHIP_GATE_FAIL: 'Gate failed',
  // Carousel director verdicts (a carousel is judged by the set-level rubric).
  GREAT: 'Great',
  STRONG: 'Strong',
  COMPETENT: 'Competent',
  TEMPLATE: 'Template',
}

export function verdictLabel(verdict: RubricVerdict): string {
  return VERDICT_LABELS[verdict] ?? verdict
}

/** Whether a verdict is a clean pass (drives the verdict pill color). */
export function isShipVerdict(verdict: RubricVerdict): boolean {
  return (
    verdict === 'SHIP' ||
    verdict === 'SHIP_WITH_ONE_FIX' ||
    // Carousel: GREAT / STRONG are the postable tiers.
    verdict === 'GREAT' ||
    verdict === 'STRONG'
  )
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined
}

/** Normalize the generated snapshot entity into the typed client view. */
export function mapSnapshot(
  entity: CreativeGenerationEntity | null | undefined
): CreativeProcessSnapshot | null {
  if (!entity) return null
  // A scoreless snapshot (a reel: a persisted revisable spec but no rubric score, since
  // there's no in-Worker video judge yet) has nothing to show in the score panel — treat
  // it as "no snapshot" so the panel stays hidden rather than rendering a 0.
  if (entity.score == null) return null
  const revisionHistory = Array.isArray(entity.revisionHistory)
    ? (entity.revisionHistory as unknown as RevisionEntry[])
    : undefined
  return {
    score: entity.score,
    // verdict is optional on the wire (nullable for scoreless reels) but always travels
    // with a score, so it is present on this scored branch; coerce for the type.
    verdict: entity.verdict ?? '',
    gates: asRecord(entity.gates) as RubricGates | undefined,
    dimensions: asRecord(entity.dimensions) as RubricDimensions | undefined,
    swapTest: asString(entity.swapTest),
    singleFix: asString(entity.singleFix),
    fixStage: asString(entity.fixStage),
    designScore: asNumber(entity.designScore),
    designTier: asString(entity.designTier),
    brief: asRecord(entity.brief),
    angle: asRecord(entity.angle),
    revisionHistory,
    metrics: asRecord(entity.metrics),
    scoredAt: asString(entity.scoredAt),
    source: asString(entity.source),
  }
}

export function mapHistory(
  dto: CreativeGenerationResponseDto | null | undefined
): ScoreHistoryPoint[] {
  const rows = dto?.history ?? []
  return rows.map(r => ({
    score: r.score,
    verdict: r.verdict,
    source: asString(r.source),
    createdAt: r.createdAt,
  }))
}
